import React, { useState } from "react";
import { useAuth } from "../../contexts/authContext/";
import { addRecord } from "../../firebase/db/shares/crud.tsx";
import WavesurferPlayer from "@wavesurfer/react";
import WaveSurfer from "wavesurfer.js";

const SongShareModal = ({ isOpen, onClose, parentId, songShareName, shareAudioUrl }) => {
	const { currentUser } = useAuth();
	const [themeClass, setThemeClass] = useState("form-body-classic");
	const [songName, setSongName] = useState(`${songShareName}`);
	const [shareComment, setShareComment] = useState(String);
	const [isWaveLoading, setIsWaveLoading] = useState<Boolean>(true);
	const [wavesurfer, setWavesurfer] = useState<WaveSurfer | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [waveColor, setWaveColor] = useState("white");

	if (!isOpen) return null;

	const handleSubmit = (e) => {
		e.preventDefault();
		const newShare = {
			comments: shareComment,
			created: new Date(),
			css_class: themeClass,
			plays: 0,
			song_name: songName,
			wave_color: waveColor,
			audio_url: shareAudioUrl,
			song_id: parentId
		};
		addRecord(currentUser, newShare);
		handleClose();
	};

	const handleClose = () => {
		onClose();
	};

	const handleThemeChange = (e) => {
		setThemeClass(e.target.value);
	};

	const handleSetSongName = (e) => {
		setSongName(e.target.value);
	};

	const handleSetShareComment = (e) => {
		setShareComment(e.target.value);
	};

	const onReady = (ws) => {
		setIsWaveLoading(false);
		setWavesurfer(ws);
		setIsPlaying(false);
		setIsWaveLoading(false);
	};

	const onPlayPause = () => {
		wavesurfer?.playPause();
	};

	return (
		<div
			className="fixed inset-0 flex
						items-center justify-center
						bg-black bg-opacity-50">
			<div id="large-modal" className="fixed items-center justify-center z-50 p-4 md:inset-0 content-center">
				<div className="relative w-full max-w-4xl max-h-full ml-auto mr-auto">
					<div className="relative bg-white rounded-lg shadow-sm dark:bg-gray-700">
						<div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600 border-gray-200">
							<h3 className="text-xl font-medium text-grey-700">New Share</h3>
							<button
								onClick={handleClose}
								type="button"
								className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
								data-modal-hide="extralarge-modal">
								<svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
									<path
										stroke="currentColor"
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
									/>
								</svg>
								<span className="sr-only">Close modal</span>
							</button>
						</div>

						<div className="modal-body flex">
							<div className="w-1/2">
								<form className="p-8 space-y-4">
									<div>
										<label for="theme-selector" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
											Theme
										</label>
										<select
											id="theme-selector"
											value={themeClass}
											onChange={handleThemeChange}
											className="bg-gray-50 border border-white-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-white-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
											<option label="Classic" value="form-body-classic" />
											<option label="Fresh" value="form-body-fresh" />
											<option label="Avocado" value="form-body-avocado" />
										</select>
									</div>
									<div>
										<label for="header-text" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
											Song Name
										</label>
										<input
											defaultValue={songName}
											onChange={handleSetSongName}
											id="header-text"
											type="text"
											required
											className="w-full mt-2 px-3 py-2 text-gray-300 bg-transparent outline-none border focus:border-indigo-600 shadow-sm rounded-lg transition duration-300"
										/>
									</div>
									<div>
										<label for="header-text" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
											Comments
										</label>
										<textarea
											defaultValue={shareComment}
											onChange={handleSetShareComment}
											id="description"
											name="description"
											rows={4}
											className="w-full mt-2 px-3 py-2 text-gray-300 bg-transparent outline-none border focus:border-indigo-600 shadow-sm rounded-lg transition duration-300"
											placeholder=" "></textarea>
									</div>
								</form>
							</div>
							<div className="w-1/2">
								<div className={`${themeClass} py-1 mt-0 pt-12 pb-12`}>
									<main className="top-auto w-full justify-center content-center flex self-center place-content-center place-items-center">
										<div style={{ transform: `scale(0.8)` }} className="form-panel w-96 space-y-5 p-8 border rounded-xl">
											<div className="text-center mb-6">
												<div className="mt-4">
													<h2 className="text-xl font-semibold sm:text-2xl">{songName}</h2>
												</div>
											</div>
											<div className="text-center">
												{isWaveLoading && (
													<div className="text-center audio-loading">
														<div role="status">
															<svg
																aria-hidden="true"
																className="w-10 h-10 absolute inline text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
																viewBox="0 0 100 101"
																fill="none"
																xmlns="http://www.w3.org/2000/svg">
																<path
																	d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
																	fill="currentColor"
																/>
																<path
																	d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
																	fill="currentFill"
																/>
															</svg>
															<span className="sr-only">Loading...</span>
														</div>
													</div>
												)}
												<WavesurferPlayer
													height={50}
													barWidth={4}
													barRadius={6}
													barGap={2}
													cursorWidth={2}
													normalize={true}
													mediaControls={false}
													waveColor={waveColor}
													progressColor="rgba(234, 178, 8, 0.87)"
													url={shareAudioUrl}
													onReady={onReady}
													onPlay={() => setIsPlaying(true)}
													onPause={() => setIsPlaying(false)}
													sampleRate={48000}
												/>
												<div className="mt-3 p-4 ">
													<button
														className="inline-flex h-[45px] w-[45px] items-center justify-center rounded-full bg-gray-800 text-white drop-shadow-sm transition-colors duration-150 hover:bg-gray-200"
														onClick={onPlayPause}>
														{isPlaying ? (
															<svg
																xmlns="http://www.w3.org/2000/svg"
																viewBox="0 0 24 24"
																width="24"
																height="24"
																fill="currentColor">
																<path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
															</svg>
														) : (
															<svg viewBox="0 0 24 24" fill="currentColor" height="24" width="24">
																<path d="M8 5v14l11-7z" />
															</svg>
														)}
													</button>
												</div>
											</div>
											<div className="text-center">{shareComment}</div>
										</div>
									</main>
									<script src="./assets/vendor/canvas-confetti/dist/confetti.browser.js"></script>
								</div>
							</div>
						</div>
						<div className="modal-footer justify-end flex items-center p-4 md:p-5 space-x-3 rtl:space-x-reverse border-t border-gray-200 rounded-b dark:border-gray-600">
							<div className="modal-footer>">
								<button
									onClick={handleClose}
									type="button"
									className="py-2.5 px-5 text-sm font-normal text-gray-600 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700">
									Cancel
								</button>
								<button
									onClick={handleSubmit}
									type="button"
									className="ml-2 py-2.5 px-5 text-sm font-semibold text-gray-400 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700">
									Generate Link
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SongShareModal;
